/**
 * AMZEL Nest — Landing v2 (offizielles UI, gemounted unter "/").
 *
 * Alternative landing page designed for non-technical Amazon stakeholders.
 * Goal: at a glance, anyone (auditor, ops manager, AMZ-Infosec) understands
 *   - what we know about each site,
 *   - how many sites and where,
 *   - and how to drill into one.
 *
 * Data source: `/eu-sites.json` (already shipped). No mocks duplicated here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  Building2,
  ChevronRight,
  Globe2,
  RadioTower,
  Search,
  X,
} from "lucide-react";

import "./i18n";
import {
  BU_LABELS,
  COUNTRY_NAMES,
  type BusinessUnit,
  type CountryISO2,
  type Site,
} from "../lib/types";
import { ShellV2 } from "./shell-v2";

// ---------------------------------------------------------------------------
// Status presentation helpers (tones only — labels via i18n).
// ---------------------------------------------------------------------------

function statusTone(status: string): string {
  switch (status) {
    case "active":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300";
    case "provisioning":
      return "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300";
    case "maintenance":
      return "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300";
    case "planned":
      return "bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300";
    case "decommissioned":
      return "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300";
    default:
      return "bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300";
  }
}

// ---------------------------------------------------------------------------
// Data loader
// ---------------------------------------------------------------------------

function useEuSites(): { sites: Site[]; loading: boolean } {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    fetch("/eu-sites.json")
      .then((r) => r.json())
      .then((data: Site[]) => {
        if (!cancelled) {
          setSites(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { sites, loading };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LandingV2() {
  const { t, i18n } = useTranslation();
  const { sites, loading } = useEuSites();
  const [country, setCountry] = useState<CountryISO2 | null>(null);
  const [bu, setBu] = useState<BusinessUnit | null>(null);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return sites.filter(
      (s) =>
        (!country || s.country === country) &&
        (!bu || s.bu === bu) &&
        (!ql ||
          s.code.toLowerCase().includes(ql) ||
          (s.address ?? "").toLowerCase().includes(ql)),
    );
  }, [sites, country, bu, q]);

  // KPI summary
  const totals = useMemo(() => {
    const countries = new Set(sites.map((s) => s.country));
    const repeaters = sites.reduce((acc, s) => acc + s.repeaters, 0);
    const active = sites.filter((s) => s.status === "active").length;
    return {
      sites: sites.length,
      countries: countries.size,
      repeaters,
      active,
    };
  }, [sites]);

  const countryCounts = useMemo(() => {
    const map = new Map<CountryISO2, number>();
    sites.forEach((s) => map.set(s.country, (map.get(s.country) ?? 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [sites]);

  const buCounts = useMemo(() => {
    const map = new Map<BusinessUnit, number>();
    sites.forEach((s) => map.set(s.bu, (map.get(s.bu) ?? 0) + 1));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [sites]);

  const anyFilter = country !== null || bu !== null || q.trim() !== "";

  // Number formatting follows the active language.
  const numLocale = ({ en: "en-GB", de: "de-DE", pl: "pl-PL", fr: "fr-FR", it: "it-IT" } as const)[
    i18n.language as "en" | "de" | "pl" | "fr" | "it"
  ] ?? "en-GB";

  return (
    <ShellV2>
      {/* Hero */}
      <section className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
          {t("landing.kicker")}
        </p>
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          {t("landing.headline")}
        </h1>
        <p className="max-w-3xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
          {t("landing.intro")}
        </p>
      </section>

      {/* KPI strip */}
      <section className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={t("landing.kpiSites")} value={totals.sites} loading={loading} locale={numLocale} />
        <Kpi label={t("landing.kpiCountries")} value={totals.countries} loading={loading} locale={numLocale} />
        <Kpi label={t("landing.kpiRepeaters")} value={totals.repeaters} loading={loading} locale={numLocale} />
        <Kpi label={t("landing.kpiActive")} value={totals.active} loading={loading} accent="ok" locale={numLocale} />
      </section>

      {/* Search + filters */}
      <section className="mt-10 space-y-5">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400 dark:text-slate-500"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("landing.searchPlaceholder")}
            className="w-full rounded-2xl border border-slate-200 bg-white py-4 pl-12 pr-12 text-base text-slate-900 placeholder:text-slate-400 shadow-sm transition-colors focus:border-slate-400 focus:outline-none focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-slate-500 dark:focus:ring-slate-700"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              aria-label={t("landing.searchReset")}
              className="absolute right-3 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <X className="size-4" aria-hidden />
            </button>
          )}
        </div>

        <div className="space-y-3">
          <FilterRow
            icon={<Globe2 className="size-4" aria-hidden />}
            title={t("landing.filterByCountry")}
          >
            {countryCounts.map(([code, count]) => (
              <Chip
                key={code}
                active={country === code}
                onClick={() => setCountry((c) => (c === code ? null : code))}
              >
                {t(`country.${code}`, { defaultValue: COUNTRY_NAMES[code] ?? code })}
                <span className="ml-1.5 text-xs text-slate-500 dark:text-slate-400">
                  {count}
                </span>
              </Chip>
            ))}
          </FilterRow>

          <FilterRow
            icon={<Building2 className="size-4" aria-hidden />}
            title={t("landing.filterByBU")}
          >
            {buCounts.map(([code, count]) => (
              <Chip
                key={code}
                active={bu === code}
                onClick={() => setBu((b) => (b === code ? null : code))}
              >
                {t(`bu.${code}`, { defaultValue: BU_LABELS[code] ?? code })}
                <span className="ml-1.5 text-xs text-slate-500 dark:text-slate-400">
                  {count}
                </span>
              </Chip>
            ))}
          </FilterRow>
        </div>

        {anyFilter && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-600 dark:text-slate-400">
              <Trans
                i18nKey="landing.filteredCount"
                values={{ n: filtered.length, total: sites.length }}
                components={[<strong key="0" className="font-semibold text-slate-900 dark:text-slate-100" />]}
              />
            </span>
            <button
              type="button"
              onClick={() => {
                setCountry(null);
                setBu(null);
                setQ("");
              }}
              className="rounded-full px-3 py-1 text-sm font-medium text-slate-700 underline-offset-4 hover:underline dark:text-slate-300"
            >
              {t("landing.resetFilters")}
            </button>
          </div>
        )}
      </section>

      {/* Site list */}
      <section className="mt-8">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
          <span>{t("landing.sitesHeading")}</span>
          <span className="font-mono text-xs">({filtered.length})</span>
        </h2>

        <SiteList loading={loading} sites={filtered} />

        {filtered.length > 60 && (
          <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
            {t("landing.showingFirst", { n: filtered.length })}
          </p>
        )}
      </section>
    </ShellV2>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Kpi({
  label,
  value,
  loading,
  accent,
  locale,
}: Readonly<{
  label: string;
  value: number;
  loading: boolean;
  accent?: "ok";
  locale: string;
}>) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="text-xs font-medium uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div
        className={`mt-1 text-3xl font-bold tracking-tight tabular-nums ${
          accent === "ok"
            ? "text-emerald-700 dark:text-emerald-400"
            : "text-slate-900 dark:text-slate-100"
        }`}
      >
        {loading ? "—" : value.toLocaleString(locale)}
      </div>
    </div>
  );
}

function FilterRow({
  icon,
  title,
  children,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}>) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {icon}
        <span>{title}</span>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: Readonly<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors " +
        (active
          ? "border-slate-900 bg-slate-900 text-white shadow-sm dark:border-white dark:bg-white dark:text-slate-900"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800")
      }
    >
      {children}
    </button>
  );
}

function SiteList({
  loading,
  sites,
}: Readonly<{ loading: boolean; sites: Site[] }>) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
        {t("landing.loading")}
      </div>
    );
  }
  if (sites.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
        {t("landing.noMatch")}
      </div>
    );
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {sites.slice(0, 60).map((s) => (
        <li key={s.code}>
          <SiteCard site={s} />
        </li>
      ))}
    </ul>
  );
}

function SiteCard({ site }: Readonly<{ site: Site }>) {
  const { t } = useTranslation();
  const detailHref = `/sites/${site.code}`;
  const buLabel = t(`bu.${site.bu}`, { defaultValue: BU_LABELS[site.bu] ?? site.bu });
  const countryLabel = t(`country.${site.country}`, { defaultValue: COUNTRY_NAMES[site.country] ?? site.country });
  const status = t(`status.${site.status}`, { defaultValue: site.status });
  return (
    <Link
      to={detailHref}
      className="group block h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {site.code}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusTone(site.status)}`}
            >
              {status}
            </span>
          </div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {countryLabel} · {buLabel}
          </div>
        </div>
        <ChevronRight
          aria-hidden
          className="size-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-500 dark:text-slate-600 dark:group-hover:text-slate-300"
        />
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
        <div className="inline-flex items-center gap-1.5">
          <RadioTower className="size-4 text-slate-400 dark:text-slate-500" aria-hidden />
          <span>
            <strong className="font-semibold text-slate-900 dark:text-slate-100">
              {site.repeaters}
            </strong>{" "}
            {t("landing.repeatersLabel")}
          </span>
        </div>
      </div>

      {site.address && (
        <p className="mt-3 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
          {site.address}
        </p>
      )}
    </Link>
  );
}
